import { Button, Form, Input, Modal, Select, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

import { Availability, createMeetingSlotsFrom } from '@/interface';

import AvailabilityDisplay from '@/pages/dashboard/project/detail/meetings/components/displayAvailableSlots';

import {
  createMeetingSlotsRequest,
  generateMeetingRequest,
  getApprovedExpertsRequest,
  getUnscheduledMeetingsRequest,
} from '@/redux/slices';
import { AppDispatch, RootState } from '@/redux/store';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Paragraph } = Typography;

interface Expert {
  id: string;
  firstName: string;
  lastName: string;
  email: string | string[];
}

interface MeetingData {
  expertId: string;
  date: dayjs.Dayjs | null;
  title: string;
  description?: string;
  timeSlot: string;
}

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  isBooked: boolean;
}

interface AvailabilityData {
  date: string;
  slots: TimeSlot[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  meeting?: Availability;
  selectedDate: Date;
}

const CreateMeetingModal: React.FC<Props> = ({ open, onClose, projectId, meeting, selectedDate }) => {
  const [form] = Form.useForm<MeetingData>();
  const { loading, success, data, error } = useSelector((state: RootState) => state.project.createmeetingSlots);
  const { generateMeeting } = useSelector((state: RootState) => state.project);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [availability, setAvailability] = useState<AvailabilityData[]>([]);
  const [descriptionEntered, setDescriptionEntered] = useState(false);
  const [slotsCreated, setSlotsCreated] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const [experts, setExperts] = useState<Expert[]>([]);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const desc = e.target.value;
    setDescriptionEntered(!!desc);
    setSlotsCreated(false);
    setAvailability([]);
  };

  const handleCreateSlots = () => {
    const desc = form.getFieldValue('description');
    const expertId = form.getFieldValue('expertId');
    const title = form.getFieldValue('title');
    const expert = experts.find((exp) => exp.id === expertId);

    if (!desc || !expert || !title) {
      toast.error('Please select expert and enter title and description first.');
      return;
    }

    const payload: createMeetingSlotsFrom = {
      title,
      expertId: expertId,
      projectId: projectId,
      description: desc,
    };
    dispatch(createMeetingSlotsRequest({ data: payload }))
      .unwrap()
      .then(() => {
        getUnscheduledMeetingsRequest({
          id: projectId,
          date: dayjs.utc(selectedDate).format('YYYY-MM-DD'),
        });
      });
  };

  React.useEffect(() => {
    if (meeting) {
      form.setFieldsValue({
        title: meeting.title || '',
        description: '',
        expertId: meeting.expert?.id,
      });

      const groupedByDate: Record<string, TimeSlot[]> = {};
      meeting.slots?.forEach((slot) => {
        const start = dayjs(slot.startTime);
        const end = dayjs(slot.endTime);
        const dateKey = start.format('YYYY-MM-DD');

        if (!groupedByDate[dateKey]) {
          groupedByDate[dateKey] = [];
        }

        groupedByDate[dateKey].push({
          id: slot.slotId,
          startTime: start.format('MM/DD/YYYY hh:mm A'),
          endTime: end.format('MM/DD/YYYY hh:mm A'),
          isBooked: false,
        });
      });

      const formattedAvailability: AvailabilityData[] = Object.keys(groupedByDate).map((date) => ({
        date,
        slots: groupedByDate[date],
      }));

      setAvailability(formattedAvailability);
      setSlotsCreated(true);
    }
  }, [meeting, form]);

  React.useEffect(() => {
    const fetchExperts = async () => {
      try {
        const response = await dispatch(getApprovedExpertsRequest({ id: projectId })).unwrap();
        setExperts(response);
      } catch (error) {
        console.error('Failed to fetch experts:', error);
      }
    };

    if (projectId) {
      fetchExperts();
    }
  }, [projectId, dispatch]);

  const onFinish = () => {
    if (!selectedSlot) {
      toast.error('Please select a time slot.');
      return;
    }

    const payload = {
      id: projectId,
      data: {
        expertId: meeting?.expert?.id || form.getFieldValue('expertId') || '',
        slotId: selectedSlot,
      },
    };

    dispatch(generateMeetingRequest(payload))
      .unwrap()
      .then(() => {
        dispatch(
          getUnscheduledMeetingsRequest({
            id: projectId,
            date: dayjs.utc(selectedDate).format('YYYY-MM-DD'),
          }),
        );
        onClose();
      });
  };

  React.useEffect(() => {
    if (success && data?.meetingInfo?.meetingSlots) {
      const groupedByDate: Record<string, TimeSlot[]> = {};
      data.meetingInfo.meetingSlots.forEach((slot) => {
        const start = dayjs(slot.startTime);
        const end = dayjs(slot.endTime);
        const dateKey = start.format('YYYY-MM-DD');
        if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
        groupedByDate[dateKey].push({
          id: slot.slotId,
          startTime: start.format('MM/DD/YYYY hh:mm A'),
          endTime: end.format('MM/DD/YYYY hh:mm A'),
          isBooked: false,
        });
      });

      const formattedAvailability: AvailabilityData[] = Object.keys(groupedByDate).map((date) => ({
        date,
        slots: groupedByDate[date],
      }));

      setAvailability(formattedAvailability);
      setSlotsCreated(true);
    }
  }, [success, data, error]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      styles={{
        body: { maxHeight: '60vh', overflowY: 'auto', background: 'var(--cardClr)' },
        content: { background: 'var(--cardClr)', color: 'var(--cardClr-text)' },
        header: { background: 'var(--cardClr)', borderBottom: '1px solid var(--cardClr-secondary)' },
      }}
      footer={
        <div className="flex justify-end gap-3 py-2 px-2">
          <Button onClick={onClose}>Cancel</Button>
          {!slotsCreated && descriptionEntered && (
            <Button
              type="primary"
              onClick={handleCreateSlots}
              loading={loading}
              style={{
                background: 'var(--primary)',
                color: 'var(--textClr-active)',
              }}
            >
              {loading ? 'Creating...' : 'Create Slots'}
            </Button>
          )}
          {slotsCreated && (
            <Button
              type="primary"
              onClick={onFinish}
              loading={generateMeeting.loading}
              style={{
                background: 'var(--primary)',
                color: 'var(--primary-text)',
              }}
            >
              {generateMeeting.loading ? 'Creating...' : 'Create Meeting'}
            </Button>
          )}
        </div>
      }
      destroyOnClose
      title={
        <Title level={4} style={{ color: 'var(--cardClr-text)' }}>
          Schedule a New Meeting
        </Title>
      }
    >
      <Toaster />
      <Spin spinning={generateMeeting.loading || loading} tip="Processing..." size="large">
        <Paragraph className="mb-6" style={{ color: 'var(--cardClr-textSecondary)' }}>
          Select an expert, enter description, then create slots. After slots are created, you can schedule the meeting.
        </Paragraph>
        <Form form={form} layout="vertical">
          {/* Expert Dropdown */}
          <Form.Item name="expertId" label="Select Expert" rules={[{ required: true }]}>
            <Select
              placeholder="Choose an expert"
              size="large"
              style={{ background: 'var(--input-bg)', color: 'var(--input-text)' }}
              dropdownStyle={{ background: 'var(--cardClr)', color: 'var(--cardClr-text)' }}
            >
              {experts.map((expert) => (
                <Option key={expert.id} value={expert.id}>
                  {expert.firstName} {expert.lastName} ({Array.isArray(expert.email) ? expert.email[0] : expert.email})
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Title */}
          <Form.Item name="title" label="Meeting Title" rules={[{ required: true }]}>
            <Input
              size="large"
              placeholder="e.g., Technical Interview - Round 1"
              style={{ background: 'var(--input-bg)', color: 'var(--input-text)', borderColor: 'var(--input-border)' }}
            />
          </Form.Item>

          {/* Description */}
          {!meeting && (
            <Form.Item name="description" label="Meeting Description" rules={[{ required: true }]}>
              <TextArea
                rows={4}
                onChange={handleDescriptionChange}
                style={{ background: 'var(--input-bg)', color: 'var(--input-text)', borderColor: 'var(--input-border)' }}
              />
            </Form.Item>
          )}

          {/* Slots */}
          {availability.length > 0 && (
            <AvailabilityDisplay
              availability={availability}
              selectedSlot={selectedSlot}
              onSelect={(slotId) => setSelectedSlot(slotId)}
            />
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateMeetingModal;
